// UploadFilesDialog — the library's upload (FRONTEND-PATTERNS §Files): ONE
// dialog where the person drops or picks files and CLASSIFIES them in the same
// place — one question per file, the one its nature needs. The family sets the
// nature by itself (a photo, a video, a voice note = media; the rest = a
// document) and the person may flip it (a photographed permit filed under
// Permits is a document). MEDIA: "What does it show?" — the picker OPEN in the
// form (the peek's surface as a plain field — the catalog: the categories as
// chips, a chip opens its things; no pencil, no Save, the footer is the one
// commit) for the capture being answered and the first still without an answer;
// an answered one folds to its chips with Change; "Use for all photos" when a
// batch shares its subject; a caption.
// A DOCUMENT: "Where?" — a folder here, or an asset here with its folder (the
// destination picker Move uses); a display name. A door that knows more (the
// shelf line, a folder heading) preselects. Nothing blocks the upload: a photo
// that comes in without an answer lands in "To review".
//
// The Modal + `useFormShell` container (ADR-038 — a dialog for one act); the
// picked files live beside the form (a File is not a form value), the answers
// are an RHF field array validated by Zod; each file uploads in turn with its
// own progress, the door's two typed refusals read as that file's error. A
// create: every open starts empty — no files, no answers.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../ui/Button.js';
import { FileUpload } from '../ui/FileUpload.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { Modal } from '../ui/Modal.js';
import { ProgressBar } from '../ui/ProgressBar.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { Tag } from '../ui/Tag.js';
import { icons } from '../ui/icons.js';
import { useToast } from '../ui/Toast.js';
import { formatBytes } from '../../lib/format.js';
import { FormBody, FormFooter } from './forms/FormShell.js';
import { useFormShell } from './forms/useFormShell.js';
import { OwnRefusal } from './forms/useRefusalLine.js';
import { FamilyGlyph, type FileNature } from './filesDisplay.js';
import { FileDestinationPicker, emptyDestination, type FileDestination } from './FileDestinationPicker.js';
import { WhatItShowsPicker, type PickedNames, type PickerTopic } from './WhatItShowsPicker.js';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { useNamedUnits } from '../../hooks/useAssets.js';
import { useResetOnOpen } from '../../hooks/useResetOnOpen.js';
import { useAssetFace } from './lookupFaces.js';

export interface UploadAnswer {
  /** A document's destination in the person's words (the toast) — the folder's name for a folder here; "" for media. */
  where: string;
  nature: FileNature;
  /** A document's folder; null for media. */
  folder: string | null;
  /** A document filed under an asset of this place (its anchor); null = this place. */
  asset_id: string | null;
  /** Media: what it shows (optional, never blocking). */
  assets: string[];
  topics: string[];
  /** A document's display name / a capture's caption. */
  display_name: string;
}

export interface UploadVars {
  file: File;
  answer: UploadAnswer;
  /** 0..1 while the bytes travel; null = the sizes phase (indeterminate). */
  onProgress: (fraction: number | null) => void;
  signal: AbortSignal;
}

type Progress = { fraction: number | null } | { done: true } | { error: string } | null;

export interface UploadFilesDialogProps {
  open: boolean;
  onClose: () => void;
  /** What the door knew: the shelf line → document (+ a folder); the phone's door
   *  → media; the ASSET tab's door → this asset preselected as what media shows. */
  preset?: { nature?: FileNature; folder?: string | null; assets?: string[] };
  /** This place's folders; an asset's vocabulary for a document filed under one. */
  folders: string[];
  /** The class of this place — a site's folders carry a restricted band, an asset's do not (the lock in the folder select). */
  folderClass?: 'site' | 'asset' | 'model';
  /** An always-filed anchor (a model's library): every pick is a document — the
   *  nature flip never shows and a photo files like any paper (ADR-068 §2's
   *  per-anchor nature rule, the 2026-09-05 ruling). */
  natureLocked?: boolean;
  assetFolders: string[];
  /** A document's "Where?" roads — the site offers a folder here · under an asset;
   *  the asset tab offers only its own folders (it IS the asset). */
  documentRoads?: ('folder' | 'asset')[];
  folderLabel: (folder: string) => string;
  projectId: string;
  /** The site whose units the questions offer (an asset's library: its site); null = a place with no units (a model's). */
  siteId: string | null;
  /** The kinds present at the site (the options read) — the picker's chips. */
  kinds?: readonly AssetCategory[];
  topicOptions: PickerTopic[];
  /** The picker's accept list (the class's sniffable types). */
  accept?: string;
  /** The effective cap per file (class ∧ family) — refused here, before bytes travel. */
  capFor: (file: File) => number;
  onUpload: (vars: UploadVars) => Promise<void>;
  /** The door's typed refusal codes → this file's error (the consumer maps the API error). */
  errorCodeOf?: (err: unknown) => string | null;
}

const AnswerSchema = z
  .object({
    nature: z.enum(['media', 'document']),
    assets: z.array(z.string()),
    topics: z.array(z.string()),
    display_name: z.string().trim().max(255),
    destination: z.custom<FileDestination>((v) => typeof v === 'object' && v !== null),
  })
  .superRefine((a, ctx) => {
    if (a.nature !== 'document') return;
    if (a.destination.road === 'asset' && !a.destination.assetId) ctx.addIssue({ code: 'custom', path: ['destination', 'asset'], message: 'asset' });
    if (!a.destination.folder) ctx.addIssue({ code: 'custom', path: ['destination', 'folder'], message: 'folder' });
  });
const FormSchema = z.object({ items: z.array(AnswerSchema) });
type FormValues = z.infer<typeof FormSchema>;

function defaultNature(file: File): FileNature {
  const t = file.type;
  return t.startsWith('image/') || t.startsWith('video/') || t.startsWith('audio/') ? 'media' : 'document';
}

export function UploadFilesDialog({ open, onClose, preset, folders, folderClass = 'site', natureLocked = false, assetFolders, documentRoads = ['folder', 'asset'], folderLabel, projectId, siteId, kinds = [], topicOptions, accept, capFor, onUpload, errorCodeOf }: UploadFilesDialogProps) {
  const { t } = useTranslation('files');
  const toast = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [sending, setSending] = useState(false);
  const [picking, setPicking] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Each document's destination in words, as its picker names it (the toast's "Uploaded to …").
  const wheres = useRef<Record<number, string>>({});
  useEffect(() => () => abortRef.current?.abort(), []);
  // The files beside the form start empty with the answers (the shell's reseed below).
  useResetOnOpen(open, (isOpen) => {
    if (!isOpen) return;
    setFiles([]);
    setProgress([]);
    setPicking(null);
  });

  const shell = useFormShell<FormValues>({
    schema: FormSchema as unknown as z.ZodType<FormValues, FormValues>,
    values: () => ({ items: [] }),
    onSubmit: async (v) => {
      setSending(true);
      const ac = new AbortController();
      abortRef.current = ac;
      let failed = 0;
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]!;
          const a = v.items[i]!;
          const document = a.nature === 'document';
          const answer: UploadAnswer = {
            where: document ? (wheres.current[i] ?? '') : '',
            nature: a.nature,
            folder: document ? a.destination.folder : null,
            asset_id: document && a.destination.road === 'asset' ? a.destination.assetId : null,
            display_name: a.display_name,
            assets: document ? [] : a.assets,
            topics: document ? [] : a.topics,
          };
          if (progress[i] && 'done' in progress[i]!) continue;
          setProgress((p) => p.map((x, j) => (j === i ? { fraction: 0 } : x)));
          try {
            await onUpload({ file, answer, signal: ac.signal, onProgress: (f) => setProgress((p) => p.map((x, j) => (j === i ? { fraction: f } : x))) });
            setProgress((p) => p.map((x, j) => (j === i ? { done: true } : x)));
          } catch (err) {
            if (ac.signal.aborted) throw err;
            failed++;
            const code = errorCodeOf?.(err);
            const message = code === 'FILE_TOO_LARGE' ? t('upload.tooLarge', { max: formatBytes(capFor(file)) }) : code === 'FILE_TYPE_NOT_ALLOWED' ? t('upload.typeNotAllowed') : t('upload.failed');
            setProgress((p) => p.map((x, j) => (j === i ? { error: message } : x)));
          }
        }
      } finally {
        abortRef.current = null;
        setSending(false);
      }
      if (failed > 0) throw new OwnRefusal(t('upload.partial')); // the dialog stays open: the line says it, each row says why
      toast.success(t('upload.doneCount', { count: files.length }));
    },
    onSuccess: onClose,
  });
  // Every open starts without answers and without the last attempt's line (a passive effect — an RHF reset).
  const { reseed } = shell;
  useEffect(() => {
    if (open) reseed();
  }, [open, reseed]);
  const { fields, append, remove } = useFieldArray({ control: shell.form.control, name: 'items' });
  const items = shell.form.watch('items');
  const mediaCount = items.filter((a) => a.nature === 'media').length;

  // The picked files and the answers move together: a new file appends its
  // defaults (the nature from the family, the preset winning); a removed one
  // takes its answer with it.
  const onFilesChange = (next: File[]) => {
    const added = next.filter((f) => !files.includes(f));
    const removedIdx = files.map((f, i) => (next.includes(f) ? -1 : i)).filter((i) => i >= 0);
    for (const i of [...removedIdx].reverse()) {
      remove(i);
      setProgress((p) => p.filter((_, j) => j !== i));
    }
    for (const f of added) {
      const nature = natureLocked ? 'document' : (preset?.nature ?? defaultNature(f));
      append({ nature, assets: preset?.assets ?? [], topics: [], display_name: '', destination: emptyDestination('folder', nature === 'document' ? (preset?.folder ?? null) : null) });
      setProgress((p) => [...p, null]);
    }
    setFiles(next);
    setPicking(null);
  };

  const tooLarge = useMemo(() => files.map((f) => f.size > capFor(f)), [files, capFor]);
  const blocked = files.length === 0 || tooLarge.some(Boolean) || sending;

  const requestClose = () => {
    if (sending) {
      abortRef.current?.abort();
      onClose();
      return;
    }
    if (files.length > 0) shell.requestLeave(onClose);
    else onClose();
  };

  const natureOptions = [
    { value: 'media', label: t('upload.natureMedia') },
    { value: 'document', label: t('upload.natureDocument') },
  ];
  // The picks' names once a picker folded: what the pickers said (`onChange` hands them), else named by id (a door's
  // preselect — the asset tab's own unit — before any picker opened).
  const [pickedNames, setPickedNames] = useState<PickedNames>(() => new Map());
  const pickedIds = useMemo(() => [...new Set(items.flatMap((a) => a.assets))], [items]);
  const named = useNamedUnits(projectId, siteId, pickedIds);
  const assetFace = useAssetFace();
  const labelOf = (id: string): string => {
    const said = pickedNames.get(id);
    if (said) return said;
    const row = named.byId.get(id);
    if (row) return assetFace(row).selectedLabel ?? assetFace(row).label;
    return topicOptions.find((tp) => tp.value === id)?.label ?? '…';
  };
  // The first capture still without an answer: its question is open by itself.
  const firstUnanswered = items.findIndex((a) => a.nature === 'media' && a.assets.length === 0 && a.topics.length === 0);

  const applyToAllMedia = (i: number) => {
    const src = items[i];
    if (!src) return;
    items.forEach((a, j) => {
      if (j !== i && a.nature === 'media') {
        shell.form.setValue(`items.${j}.assets`, [...src.assets], { shouldDirty: true });
        shell.form.setValue(`items.${j}.topics`, [...src.topics], { shouldDirty: true });
      }
    });
  };

  return (
    <>
      <Modal open={open} onClose={requestClose} title={t('upload.title')} maxWidth="max-w-2xl" footer={<FormFooter shell={shell} onCancel={requestClose} submitLabel={files.length ? t('upload.submitCount', { count: files.length }) : t('upload.submit')} submitDisabled={blocked} />}>
        <FormBody shell={shell}>
          <FileUpload multiple files={files} onFilesChange={onFilesChange} accept={accept} disabled={sending} label={t('upload.drop')} hint={t('upload.hintLibrary')} removeLabel={t('upload.remove')} />
          {fields.map((field, i) => {
            const file = files[i];
            const a = items[i];
            if (!file || !a) return null;
            const nature = a.nature;
            const state = progress[i];
            const glyphFamily = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : file.type === 'application/pdf' ? 'pdf' : 'document';
            const picks = [...a.assets, ...a.topics];
            const questionOpen = picking === i || i === firstUnanswered;
            return (
              <div key={field.id} className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--color-text-secondary)]">
                    <FamilyGlyph family={glyphFamily} contentType={file.type} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{file.name}</span>
                  <span className="text-xs text-[var(--color-text-secondary)]">{formatBytes(file.size)}</span>
                  {!natureLocked && (
                    <Controller
                      control={shell.form.control}
                      name={`items.${i}.nature`}
                      render={({ field: f }) => (
                        <SegmentedControl
                          size="sm"
                          ariaLabel={t('upload.nature')}
                          options={natureOptions}
                          value={f.value}
                          onChange={(v) => {
                            f.onChange(v as FileNature);
                            if (v === 'document' && !a.destination.folder) shell.form.setValue(`items.${i}.destination`, emptyDestination('folder', preset?.folder ?? null));
                            setPicking(null);
                          }}
                          disabled={sending}
                        />
                      )}
                    />
                  )}
                </div>
                {tooLarge[i] && (
                  <p role="alert" className="text-sm text-[var(--color-danger)]">
                    {t('upload.tooLarge', { max: formatBytes(capFor(file)) })}
                  </p>
                )}
                {nature === 'document' ? (
                  <Controller
                    control={shell.form.control}
                    name={`items.${i}.destination`}
                    render={({ field: f, fieldState }) => {
                      const errs = fieldState.error as unknown as Partial<Record<'asset' | 'folder', { message?: string }>> | undefined;
                      return (
                        <div className="flex flex-col gap-2">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{t('upload.where')}</span>
                          <FileDestinationPicker
                            projectId={projectId}
                            siteId={siteId ?? ''}
                            roads={siteId ? documentRoads : documentRoads.filter((r) => r !== 'asset')}
                            value={f.value}
                            onChange={f.onChange}
                            document
                            folders={folders}
                            folderClass={folderClass}
                            siteFolders={folders}
                            assetFolders={assetFolders}
                            folderLabel={folderLabel}
                            disabled={sending}
                            onWhere={(w) => {
                              wheres.current[i] = w;
                            }}
                            errors={{ asset: errs?.asset ? t('move.pickAsset') : undefined, folder: errs?.folder ? t('upload.folderRequired') : undefined }}
                          />
                        </div>
                      );
                    }}
                  />
                ) : questionOpen ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">{t('upload.shows')}</span>
                      {picks.length > 0 && mediaCount > 1 && (
                        <Button variant="link" onClick={() => applyToAllMedia(i)} disabled={sending}>
                          {t('upload.useForAll')}
                        </Button>
                      )}
                    </div>
                    {siteId && (
                      <WhatItShowsPicker
                        projectId={projectId}
                        siteId={siteId}
                        kinds={kinds}
                        topics={topicOptions}
                        value={{ assets: a.assets, topics: a.topics }}
                        onChange={(v, names) => {
                          shell.form.setValue(`items.${i}.assets`, v.assets, { shouldDirty: true });
                          shell.form.setValue(`items.${i}.topics`, v.topics, { shouldDirty: true });
                          setPickedNames((prev) => new Map([...prev, ...names]));
                          setPicking(i);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--color-text-secondary)]">{t('upload.shows')}</span>
                    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                      {picks.length > 0 ? picks.map((id) => <Tag key={id}>{labelOf(id)}</Tag>) : <span className="text-sm text-[var(--color-text-secondary)]">{t('upload.notDescribed')}</span>}
                    </span>
                    {picks.length > 0 && mediaCount > 1 && (
                      <Button variant="link" onClick={() => applyToAllMedia(i)} disabled={sending}>
                        {t('upload.useForAll')}
                      </Button>
                    )}
                    <Button variant="link" onClick={() => setPicking(i)} disabled={sending}>
                      {picks.length > 0 ? t('upload.change') : t('upload.describe')}
                    </Button>
                  </div>
                )}
                <div>
                  <Label htmlFor={`${field.id}-name`}>{nature === 'document' ? t('upload.name') : t('upload.caption')}</Label>
                  <Input id={`${field.id}-name`} {...shell.form.register(`items.${i}.display_name`)} placeholder={nature === 'document' ? file.name : ''} disabled={sending} />
                </div>
                {state && 'fraction' in state && (state.fraction === null ? <ProgressBar label={t('upload.sizes')} /> : <ProgressBar value={Math.round(state.fraction * 100)} label={t('upload.progress')} />)}
                {state && 'done' in state && (
                  <p className="flex items-center gap-1.5 text-sm text-[var(--color-success)]">
                    <icons.statusSuccess size={16} aria-hidden />
                    {t('upload.doneOne')}
                  </p>
                )}
                {state && 'error' in state && (
                  <p role="alert" className="text-sm text-[var(--color-danger)]">
                    {state.error}
                  </p>
                )}
              </div>
            );
          })}
        </FormBody>
      </Modal>
      {shell.discardDialog}
    </>
  );
}
